import { useCallback, useEffect, useMemo, useState } from 'react';
import React from 'react';

import { Box, Flex, useTheme } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import InitiativeVisibility from 'components/RabbitInitiativeCreationPanel/PrimaryPanel/AdditionalInfoSection/InitiativeVisibility';
import InitiativeCreationDescription from 'components/RabbitInitiativeCreationPanel/PrimaryPanel/DetailsSection/InitiativeCreationDescription';
import { InitiativeName } from 'components/RabbitInitiativeCreationPanel/PrimaryPanel/DetailsSection/InitiativeName';
import { InputLabelHorizontal } from 'components/RabbitInitiativeCreationPanel/components/InputLabelHorizontal';
import { DetailsSectionHeader } from 'components/RabbitInitiativeCreationPanel/components/SectionHeaders';
import { CreateInitiativePanelHeader } from 'components/RabbitInitiativeSidePanel/Components/SectionHeaders';
import { InitiativeDueDate } from 'components/RabbitInitiativeSidePanel/PrimaryPanel/Sections/AdditionalInfo/InitiativeDueDate';
import { InitiativeStartDate } from 'components/RabbitInitiativeSidePanel/PrimaryPanel/Sections/AdditionalInfo/InitiativeStartDate';
import { useCreateNewInitiative } from 'components/RabbitInitiativeSidePanel/datahooks/useInitiativeSubtasks';
import { OKROwnerAndCollaboratorComponent } from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/Details/OKROwnerAndCollaborator';
import { QUERY_KEYS } from 'components/RabbitOKRSidePanel/datahooks';
import { closeRealignPanel, onDetailsChange } from 'components/RabbitOKRSidePanel/krDrawerSecondaryPanelSlice';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import { useDispatch, useSelector } from 'react-redux';
import { kRDrawerSecondaryPanelSelector } from 'selectors/krSecondaryPanel';

import { SaveAndExit } from 'assets/assetsV1/iconComponents';

import { MAX_OKR_NAME_LENGTH, MIN_OKR_NAME_LENGTH } from 'constants/ProjectConstants';
import { sendAddedInitiativesInKrEvent, sendCreatedInitiative } from 'constants/mixpanelHelper';

import { showErrorSnackbar, showSuccessSnackbar } from 'services/snackbar';

import { Accordion, AccordionButton, AccordionItem, AccordionPanel } from 'uiToolkitV1/Accordion';
import { DrawerFooter } from 'uiToolkitV1/BottomDrawer';
import { Button } from 'uiToolkitV1/Button';
import { Divider } from 'uiToolkitV1/Divider';
import { TextLabel } from 'uiToolkitV1/Typography';

export const CreateOKRInitiativePanel = () => {
  const { type, initiativeSubtaskDetails } = useSelector(kRDrawerSecondaryPanelSelector);
  const dispatch = useDispatch();

  const onCollapse = () => {
    dispatch(closeRealignPanel()); // THIS CLOSE OKR SIDE PANEL
  };

  const theme = useTheme();

  const queryClient = useQueryClient();
  const page = useViewPageName();

  const { mutate, isMutating } = useCreateNewInitiative();

  const handleSave = async ({ initiativeDetails, onSuccess }) => {
    /* Queries to invalidate
      1.1 OKR ( so that parent and timeline component reflects updated )
      1.2 OKR so that progress updates can reflect ( in case of derived progress)
      2. refetch alignment section.
    */
    /*   const okrQueryKey = [QUERY_KEYS.KEY_RESULT, okrUuid];
    const alignmentQueryKey = [QUERY_KEYS.OKR_TOP_HIERARCHY, okrUuid]; */

    mutate(
      {
        body: initiativeDetails,
      },
      {
        onError: (err) => {
          console.error(err.message);
          dispatch(showErrorSnackbar(err.message || `Failed to create new Initiative`));
        },
        onSuccess: (data) => {
          const newlyCreatedInitiative = data?.entity;
          /* queryClient.invalidateQueries(okrQueryKey);
            queryClient.invalidateQueries(alignmentQueryKey); */

          sendAddedInitiativesInKrEvent({
            eventProperties: {
              goalId: initiativeDetails?.parentPost?.goal?.[0]?.uuid,
              page,
            },
          });

          sendCreatedInitiative({
            eventProperties: {
              page: initiativeDetails?.page || page,
              source: initiativeDetails?.source || 'Edit KR Panel',
              type: initiativeDetails?.type,
            },
          });

          queryClient.invalidateQueries([QUERY_KEYS.OKR_INITIATIVES]);

          dispatch(showSuccessSnackbar(`Initiative created successfully.`));
          if (typeof onSuccess === 'function') {
            onSuccess(newlyCreatedInitiative);
          }
        },
      }
    );
  };

  const containerStyles = {
    height: '100%',
    flex: 'auto 1 1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '12px 8px',
    gap: '12px',

    background: '#FFFFFF',
    borderRadius: '8px',

    overflowY: 'auto',
  };

  const { description, text, toIds = [], visibilityType, startTS, dueTS, timeBoundDetails } = initiativeSubtaskDetails;

  const [error, setError] = useState('');

  const updatedToIds = useMemo(
    () =>
      toIds.map((data) => ({
        id: data?.uuid,
        displayName: data.displayName || data.metadata.displayName,
        email: data.email || data.metadata?.email,
        profilePhoto: data.profilePhoto || data?.profile?.profilePhoto,
        ownerType: data?.ownerType,
      })),
    [toIds]
  );

  const onChange = useCallback(
    (updates) => {
      dispatch(onDetailsChange(updates));
    },
    [dispatch]
  );

  const handleSaveAndExit = () => {
    handleSave({ initiativeDetails: initiativeSubtaskDetails, onSuccess: onCollapse });
  };

  useEffect(() => {
    if (text.length < MIN_OKR_NAME_LENGTH) {
      setError('Title cannot be empty.');
      return;
    }

    if (text.length > MAX_OKR_NAME_LENGTH) {
      setError(`Title cannot be more than ${MAX_OKR_NAME_LENGTH} characters`);
      return;
    }

    setError('');
  }, [text]);

  const disabled = error?.length > 0;

  return (
    <Flex backgroundColor="#FFFFFF" h="100%" flexDirection="column">
      <CreateInitiativePanelHeader headerText="Add Initiative" onCollapse={onCollapse} />
      <Divider sx={{ borderColor: '#CFD7DF' }} />
      <Box {...containerStyles}>
        <Box w="100%">
          <DetailsSectionHeader />
          <Box>
            <InitiativeName text={text} onChange={(text) => onChange({ text })} />
          </Box>
        </Box>
        <Divider sx={{ borderColor: '#CFD7DF' }} />

        <Flex flexDirection="column" alignItems="stretch" justifyContent="center" gap="12px" flexWrap="nowrap" w="100%">
          <Accordion defaultIndex={[0]} allowMultiple style={{ boxShadow: 'none' }}>
            <AccordionItem style={{ borderBottom: 'none' }}>
              <AccordionButton style={{ background: '#FFFFFF' }}>
                <TextLabel bold color="text.g1">
                  Add Additional Info
                </TextLabel>
              </AccordionButton>
              <AccordionPanel style={{ borderTop: 'none', padding: '0px 16px' }}>
                <InputLabelHorizontal labelText="Owners:">
                  <OKROwnerAndCollaboratorComponent
                    isOwner
                    ownersAndCollaborators={updatedToIds}
                    onChange={(toIds) => {
                      const updatedOwners = toIds.map((owners) => ({ ...owners, uuid: owners.id }));

                      onChange({ toIds: updatedOwners });
                    }}
                  />
                </InputLabelHorizontal>
                <InputLabelHorizontal labelText="Collaborators:">
                  <OKROwnerAndCollaboratorComponent
                    isOwner={false}
                    ownersAndCollaborators={updatedToIds}
                    onChange={(toIds) => {
                      const updatedOwners = toIds.map((owners) => ({ ...owners, uuid: owners.id }));

                      onChange({ toIds: updatedOwners });
                    }}
                  />
                </InputLabelHorizontal>

                <InputLabelHorizontal labelText="Visibility:">
                  <InitiativeVisibility
                    visibilityType={visibilityType}
                    onChange={(visibilityType) => onChange({ visibilityType })}
                  />
                </InputLabelHorizontal>
                <InitiativeCreationDescription
                  description={description}
                  onChange={(description) => onChange({ description })}
                />
                <InputLabelHorizontal labelText="Start Date:">
                  <InitiativeStartDate
                    dueTS={dueTS}
                    startTS={startTS}
                    isClearable
                    timeBoundDetails={timeBoundDetails}
                    onChange={(startTS) => onChange({ startTS })}
                  />
                </InputLabelHorizontal>
                <InputLabelHorizontal labelText="Due Date:">
                  <InitiativeDueDate
                    dueTS={dueTS}
                    startTS={startTS}
                    isClearable
                    timeBoundDetails={timeBoundDetails}
                    onChange={(dueTS) => onChange({ dueTS })}
                  />
                </InputLabelHorizontal>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Flex>
      </Box>

      <DrawerFooter
        borderTopWidth="1px"
        h="72px"
        backgroundColor={theme.colors.neutral.F5F7F9}
        style={{ padding: '12px 16px 12px 16px' }}
      >
        <Button variant="tertiary" mr="auto" onClick={onCollapse}>
          Cancel
        </Button>
        <Button
          variant="filled"
          StartIcon={SaveAndExit}
          onClick={handleSaveAndExit}
          disabled={disabled}
          style={{ padding: '8px 12px 8px 8px' }}
        >
          Save & Exit
        </Button>
        {/* <SaveAndAddComponent disabled={disableSubmit} /> */}
      </DrawerFooter>
    </Flex>
  );
};

export default CreateOKRInitiativePanel;
